import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import { Cancel as CancelIcon, Add as AddIcon, Save as SaveIcon, Public as PublicIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  GlobalLeague,
  CreateGlobalLeagueInput,
  UpdateGlobalLeagueInput,
  globalLeagueAdminService,
} from '@/services/globalLeagueService';

interface GlobalLeagueFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (league: GlobalLeague) => void;
  editingLeague: GlobalLeague | null;
}

const detectPlatform = (url: string): 'GOTSPORT' | 'SINCSPORTS' | 'MODULAR11' | 'TGS' | null => {
  if (!url) return null;
  const normalized = url.toLowerCase();

  // Dev mode: allow localhost URLs with ?platform= query param
  if (import.meta.env.DEV && normalized.includes('localhost')) {
    try {
      const urlObj = new URL(url);
      const platform = urlObj.searchParams.get('platform')?.toLowerCase();
      if (platform === 'gotsport') return 'GOTSPORT';
      if (platform === 'sincsports') return 'SINCSPORTS';
      if (platform === 'modular11') return 'MODULAR11';
      if (platform === 'tgs') return 'TGS';
    } catch {
      // Invalid URL, continue with normal detection
    }
  }

  if (normalized.includes('gotsport.com') || normalized.includes('gotsoccer.com')) {
    return 'GOTSPORT';
  }
  if (normalized.includes('sincsports.com')) {
    return 'SINCSPORTS';
  }
  if (normalized.includes('modular11.com')) {
    return 'MODULAR11';
  }
  if (normalized.includes('totalglobalsports.com') || normalized.includes('athleteone.com') || normalized.includes('theecnl.com')) {
    return 'TGS';
  }
  return null;
};


const GlobalLeagueFormDialog: React.FC<GlobalLeagueFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  editingLeague,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    logoUrl: '',
    playoffsEnabled: false,
    playoffSpots: 4,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detectedPlatform = detectPlatform(formData.website);
  const isEditing = !!editingLeague;

  // Reset form when dialog opens/closes or editing league changes
  useEffect(() => {
    if (open) {
      if (editingLeague) {
        setFormData({
          name: editingLeague.name || '',
          website: editingLeague.website || '',
          logoUrl: editingLeague.logoSourceUrl || '',
          playoffsEnabled: editingLeague.playoffsEnabled || false,
          playoffSpots: editingLeague.playoffSpots || 4,
        });
      } else {
        setFormData({
          name: '',
          website: '',
          logoUrl: '',
          playoffsEnabled: false,
          playoffSpots: 4,
        });
      }
      setFieldErrors({});
      setError(null);
    }
  }, [open, editingLeague]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-populate league name when entering a known URL
      if (field === 'website' && !isEditing && (!prev.name || prev.name === autoNameFor(prev.website))) {
        const autoName = autoNameFor(value);
        if (autoName) updated.name = autoName;
      }
      return updated;
    });
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    setError(null);
  };

  // Returns a suggested league name based on URL, or empty string
  const autoNameFor = (url: string): string => {
    if (!url) return '';
    const lower = url.toLowerCase();
    if (lower.includes('theecnl.com')) return 'ECNL';
    return '';
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'League name is required';
    }

    if (!formData.website.trim()) {
      errors.website = 'Website URL is required';
    } else if (!detectedPlatform) {
      errors.website = 'URL must be a GotSport, SincSports, Modular11, or ECNL/TGS URL';
    }

    if (formData.playoffsEnabled && (!formData.playoffSpots || formData.playoffSpots < 1)) {
      errors.playoffSpots = 'Playoff spots must be at least 1';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setError(null);

    try {
      if (isEditing) {
        // Update existing league
        const input: UpdateGlobalLeagueInput = {
          name: formData.name,
          logoUrl: formData.logoUrl || undefined,
          playoffsEnabled: formData.playoffsEnabled,
          playoffSpots: formData.playoffsEnabled ? formData.playoffSpots : undefined,
        };
        const league = await globalLeagueAdminService.updateGlobalLeague(editingLeague!.id, input);
        onSuccess(league);
      } else {
        // Create league — discovery runs in the background on the server.
        // The card will appear with "In Progress" status and update via SSE.
        const input: CreateGlobalLeagueInput = {
          name: formData.name,
          website: formData.website,
          logoUrl: formData.logoUrl || undefined,
          playoffsEnabled: formData.playoffsEnabled,
          playoffSpots: formData.playoffsEnabled ? formData.playoffSpots : undefined,
        };

        const league = await globalLeagueAdminService.createGlobalLeagueWithProgress(input);
        onSuccess(league);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save league');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableAutoFocus>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditing ? <EditIcon /> : <PublicIcon />}
          {isEditing ? 'Edit Global League' : 'Add Global League'}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website URL"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                error={!!fieldErrors.website}
                helperText={
                  fieldErrors.website ||
                  'Enter a GotSport, SincSports, Modular11, or ECNL URL'
                }
                disabled={submitting || isEditing}
                required
                InputProps={{
                  endAdornment: detectedPlatform && (
                    <Chip
                      label={detectedPlatform === 'GOTSPORT' ? 'GotSport' : detectedPlatform === 'SINCSPORTS' ? 'SincSports' : detectedPlatform === 'TGS' ? 'ECNL / TGS' : 'Modular11'}
                      size="small"
                      color={detectedPlatform === 'GOTSPORT' ? 'primary' : 'secondary'}
                    />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="League Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
                disabled={submitting}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Logo URL (optional)"
                value={formData.logoUrl}
                onChange={(e) => handleChange('logoUrl', e.target.value)}
                disabled={submitting}
                placeholder="https://example.com/logo.png"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.playoffsEnabled}
                    onChange={(e) => handleChange('playoffsEnabled', e.target.checked)}
                    disabled={submitting}
                  />
                }
                label="Enable Playoffs"
              />
            </Grid>

            {formData.playoffsEnabled && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Playoff Spots"
                  value={formData.playoffSpots}
                  onChange={(e) => handleChange('playoffSpots', parseInt(e.target.value) || 0)}
                  error={!!fieldErrors.playoffSpots}
                  helperText={fieldErrors.playoffSpots}
                  disabled={submitting}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={submitting} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !formData.name.trim() || (!isEditing && !detectedPlatform)}
          startIcon={submitting ? <CircularProgress size={20} /> : isEditing ? <SaveIcon /> : <AddIcon />}
        >
          {submitting
            ? 'Saving...'
            : isEditing
            ? 'Save Changes'
            : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalLeagueFormDialog;
